import { Suit, Rank, Color, CardData } from '../types/index.js';

export class Card {
  readonly suit: Suit;
  readonly rank: Rank;
  readonly color: Color;
  readonly id: string;
  private _isFaceUp: boolean;

  constructor(suit: Suit, rank: Rank, isFaceUp: boolean = false) {
    this.suit = suit;
    this.rank = rank;
    this.color = this.determineColor(suit);
    this.id = this.generateId(suit, rank);
    this._isFaceUp = isFaceUp;
  }

  private determineColor(suit: Suit): Color {
    return suit === Suit.HEARTS || suit === Suit.DIAMONDS
      ? Color.RED
      : Color.BLACK;
  }

  private generateId(suit: Suit, _rank: Rank): string {
    const suitChar = {
      [Suit.SPADES]: 'S',
      [Suit.HEARTS]: 'H',
      [Suit.DIAMONDS]: 'D',
      [Suit.CLUBS]: 'C'
    }[suit];

    const rankStr = this.getRankDisplay();
    return `${suitChar}${rankStr}`;
  }

  get isFaceUp(): boolean {
    return this._isFaceUp;
  }

  flip(): void {
    this._isFaceUp = true;
  }

  flipDown(): void {
    this._isFaceUp = false;
  }

  // Get display string for rank (A, 2-10, J, Q, K)
  getRankDisplay(): string {
    switch (this.rank) {
      case Rank.ACE:
        return 'A';
      case Rank.JACK:
        return 'J';
      case Rank.QUEEN:
        return 'Q';
      case Rank.KING:
        return 'K';
      default:
        return this.rank.toString();
    }
  }

  // Color checks
  isRed(): boolean {
    return this.color === Color.RED;
  }

  isBlack(): boolean {
    return this.color === Color.BLACK;
  }

  // Klondike stacking rules

  /**
   * Check if this card can be placed on a tableau pile
   * Rules: Descending rank, alternating colors
   */
  canStackOnTableau(other: Card): boolean {
    // Must be one rank lower
    if (this.rank !== other.rank - 1) {
      return false;
    }

    // Must be opposite color
    return this.color !== other.color;
  }

  /**
   * Check if this card can be placed on a foundation pile
   * Rules: Ascending rank, same suit, starts with Ace
   */
  canStackOnFoundation(other: Card | null): boolean {
    if (other === null) {
      // Empty foundation: only Ace allowed
      return this.rank === Rank.ACE;
    }

    // Must be same suit
    if (this.suit !== other.suit) {
      return false;
    }

    // Must be exactly one rank higher
    return this.rank === other.rank + 1;
  }

  /**
   * Check if this card can be placed on an empty tableau pile
   * Rules: Only King allowed
   */
  canPlaceOnEmptyTableau(): boolean {
    return this.rank === Rank.KING;
  }

  // Serialization
  toData(): CardData {
    return {
      suit: this.suit,
      rank: this.rank,
      color: this.color,
      isFaceUp: this._isFaceUp,
      id: this.id
    };
  }

  static fromData(data: CardData): Card {
    const card = new Card(data.suit, data.rank, data.isFaceUp);
    return card;
  }

  // Utility
  toString(): string {
    return `${this.getRankDisplay()}${this.suit}`;
  }

  equals(other: Card): boolean {
    return this.id === other.id;
  }
}
